import styled from '@emotion/styled'
import { PBSTCK_BIDDER_SEAT_CODE } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { CatalogItemType } from '@pubstack/common/src/catalogItem'
import { MEDIA_TYPES, MediaType } from '@pubstack/common/src/media-types'
import { AdUnitEnv } from '@pubstack/common/src/pbs/adunit'
import { SITE_AD_UNIT_ENV_COMPATIBILITY, SITE_ENVS, SiteEnv } from '@pubstack/common/src/site'
import { UPGRADE_OFFER_FEATURE, UPGRADE_OFFER_LABEL, UpgradeOffer } from '@pubstack/common/src/upgrade'
import { Chip, Title } from '@mantine/core'
import { useAtomValue } from 'jotai'
import { FunctionComponent, PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useBidderSeats } from '~/api/bidder-seats.api'
import { useCatalog } from '~/api/catalog.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { CatalogStatus, CatalogWrapper } from '~/components/_styled/catalog'
import Button from '~/components/Button'
import { CardIntegration, MODULE_CARD_WIDTH_PX, ModuleCard } from '~/components/catalog/ModuleCard'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Link } from '~/components/Link'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { UpgradeModal } from '~/modules/monetization/upgrade/UpgradeModal'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { PageContent } from '~/components/PageContent'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Skeleton } from '~/components/Skeleton'
import { useToast } from '~/components/Toast/useToasts'
import { Tooltip } from '~/components/Tooltip'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PATH_BY_PRODUCT } from '~/hooks/useProduct'
import { useScope } from '~/hooks/useScope'
import { useSparkStaffAccess } from '~/hooks/useSparkBidder'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { useSparkForPubStaffAccess } from '~/hooks/useSparkForPub'
import { configurationState } from '~/state'
import { PRODUCT_MAIL } from '~/utils/constants'
import { CreateSeatFlyout } from '../common/CreateSeatFlyout'
import { FilterBar } from '../common/FilterBar'
import { seatCardAvailability } from '../common/seatSetupOption'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const FlexTooltip = styled(Tooltip)`
  display: flex;
`

const EmptyStateMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

/**
 * Heads a catalog section.
 *
 * The three sections answer different questions — what to look at first, what can be bought, what
 * cannot — and a row of cards under a line of body text read as one long grid. A heading with a rule
 * under it is what separates them at a glance, at the width the grid actually is.
 */
const SectionTitle: FunctionComponent<PropsWithChildren> = ({ children }) => (
  <Title order={4} className={'border-platinum text-ink m-0 border-b pb-2'}>
    {children}
  </Title>
)

// Constants
const SEAT_CREATED_TOAST_DURATION_MILLIS = 8000
const ILLUSTRATION_ICON_SIZE_PX = 190
const FLYOUT_WIDTH_PX = 600

// Tracking labels. The page is product-neutral now, so `common` is the prefix — `adm-`/`pbs-` would
// claim a product the URL no longer carries.
const ADD_MODULE_TRACKING_LABEL: TrackingLabel = 'common-catalog-seat-new'
const RESET_FILTERS_TRACKING_LABEL: TrackingLabel = 'common-catalog-filters-restore'
const PRODUCT_MAIL_TRACKING_LABEL: TrackingLabel = 'common-catalog-send-product-mail-apply'
const BACK_TO_SITE_TRACKING_LABEL: TrackingLabel = 'common-catalog-back-to-site-navigate'
const GO_TO_MY_BIDDER_SEATS_TRACKING_LABEL: TrackingLabel = 'common-catalog-my-bidder-seats-navigate'
const LOCKED_MODULE_TRACKING_LABEL: TrackingLabel = 'common-catalog-locked-bidder-view'

/**
 * Stands in where a company has no tagline of its own — 15 entries, including installed and featured
 * ones, previously shared the single sentence "A demand partner in our catalog.", which said nothing
 * and said the same nothing on every one of them, so the description line was noise exactly where the
 * reader had least to go on.
 *
 * Derived from what the catalog already knows about the entry rather than written per bidder: accurate
 * by construction, never stale, and it covers whatever is added next without anyone sourcing copy. It
 * describes reach, not the company — real marketing copy from the partner is still the better line,
 * and any entry that gains a `tagline` keeps using it instead of this.
 *
 * Deliberately not the word "available": this describes what the *bidder* offers, while `integrations`
 * includes the platforms this scope cannot create. Saying "available client-side" would contradict the
 * "Client-side (not on this scope)" in the card's own compat sheet, and collide with the Available /
 * Not available chips, which mean something else again — whether a seat can be created at all.
 */
const listWords = (words: string[]): string => (words.length <= 1 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`)

/** Spelled out rather than lowercased wholesale: Spark for Pubs is a product name mid-sentence. */
const INTEGRATION_PHRASE: Record<CardIntegration, string> = {
  'Client-side': 'client-side',
  'Server-side': 'server-side',
  'Spark for Pubs': 'Spark for Pubs',
  'Spark Demand': 'Spark Demand',
}

/**
 * What a module says when it says nothing of its own.
 *
 * `derivedTagline` below describes demand — the media types and platforms a bidder brings — which is the
 * one thing a wrapper, an ad server or an identity module does not. Sent to the partner's own
 * documentation instead: Pubstack integrates these, it does not write their copy, and a sentence
 * invented here would be a claim about someone else's product.
 */
const MODULE_TAGLINE = 'Please refer to the partner documentation.'

/** A bidder is the only entry the derived line describes; everything else is a module — see `MODULE_TAGLINE`. */
const isBidder = (type: CatalogItemType): boolean => type === 'prebidBidder'

const derivedTagline = (mediaTypes: MediaType[], integrations: CardIntegration[]): string => {
  const media = listWords(mediaTypes.map((mediaType) => mediaType.toLowerCase()))
  const platforms = listWords(integrations.map((integration) => INTEGRATION_PHRASE[integration]))
  if (!media) {
    return 'A demand partner in our catalog.'
  }
  return platforms ? `${platforms[0].toUpperCase()}${platforms.slice(1)} demand for ${media}.` : `Demand for ${media}.`
}

/** How many entries the Featured row picks at random to keep Spark company — see `featuredIds`. */
const FEATURED_RANDOM_COUNT = 2

/**
 * Fills the Featured row when there are not enough Spark for Pubs bidders left. Matched loosely: Xandr
 * still answers to `appnexus`, Magnite to `rubicon`.
 */
const FEATURED_FALLBACKS: readonly (readonly string[])[] = [
  ['xandr', 'appnexus'],
  ['magnite', 'rubicon'],
]

const sameIds = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every((id) => b.has(id))

/**
 * Which of the card's Env tiles light up for a bidder.
 *
 * The tiles are sites: web, iOS, Android, AMP. The bidder declares ad units: web, s2s, inapp, amp.
 * The two lists are not the same, which is why this function exists. It reuses the table a site
 * already uses to know which ad units it can carry, instead of inventing a second one. An iOS site
 * and an Android site both carry `inapp` ad units, so one bidder value lights up both tiles.
 *
 * A bidder with no `supportedEnvs` lights every tile. A missing value means the catalog has no data,
 * not that the bidder runs nowhere.
 */
const compatibleSiteEnvs = (supportedEnvs: AdUnitEnv[] | undefined): SiteEnv[] =>
  supportedEnvs === undefined ? [...SITE_ENVS] : SITE_ENVS.filter((siteEnv) => SITE_AD_UNIT_ENV_COMPATIBILITY[siteEnv].some((adUnitEnv) => supportedEnvs.includes(adUnitEnv)))

/**
 * What a catalog entry is to this scope, and the only thing the page is filtered by.
 *
 * Chips rather than a dropdown: there are four of them, they are the page's primary cut, and a chip row
 * says what the sections below are called without the reader having to open anything.
 */
const SECTION_FILTERS = [
  { value: 'all', label: 'All', trackingLabel: 'common-catalog-section-all-apply' },
  { value: 'installed', label: 'Installed', trackingLabel: 'common-catalog-section-installed-apply' },
  { value: 'available', label: 'Available', trackingLabel: 'common-catalog-section-available-apply' },
  { value: 'unavailable', label: 'Not available', trackingLabel: 'common-catalog-section-unavailable-apply' },
] as const satisfies readonly { value: string; label: string; trackingLabel: TrackingLabel }[]

type SectionFilter = (typeof SECTION_FILTERS)[number]['value']

const TYPE_OPTIONS: SelectOptionProp[] = [
  { label: 'All', value: 'all', trackingLabel: 'common-catalog-type-all-apply' },
  { label: 'Spark for Pubs', value: 'sparkForPub', trackingLabel: 'common-catalog-type-spark-for-pub-apply' },
]

/**
 * What the lock tooltip says on a bidder with no setup option at all — aliasing disabled, neither
 * server-side nor Spark compatible. `seatCardAvailability` reports nothing locked for it (there was
 * nothing to lock), but nothing can be created either, so the card is bucketed as Not available here
 * rather than sitting undimmed under Available promising a seat it cannot deliver.
 */
const NO_SETUP_OPTIONS_REASON = 'Contact us to integrate this bidder.'
const STAFF_ONLY_REASON = 'Seats on Node are created by Pubstack. Contact us.'

/**
 * The bidder catalog, for the whole scope rather than for one product.
 *
 * It was already one component mounted on two routes: what a bidder can be integrated as has always
 * been a scope-level question, and both copies read the same `adm` catalog and both products' seats.
 * Only the URL still claimed otherwise.
 */
export const CatalogPage: FunctionComponent = () => {
  // Context
  const [searchParams] = useSearchParams()
  const backToSite = searchParams.get('backToSite')
  const user = useAuthenticatedUser()
  const scopeId = user.currentScopeId
  const scope = useScope()

  // State
  const [search, setSearch] = useState<string>('')
  const [section, setSection] = useState<SectionFilter>('all')
  const [typeFilter, setTypeFilter] = useState<string>(searchParams.get('type') || 'all')

  // API Hooks
  // What a bidder can be integrated as is scope-level, whichever route this is mounted on. `adm` is
  // the superset — the pbs catalog only lists server-side-compatible bidders, which would hide every client-side-only adapter.
  const { data: modules, isLoading: isLoadingCatalog } = useCatalog('adm')
  const { data: admBidderSeats, isLoading: isLoadingAdmSeats, refetch: refetchAdmSeats } = useBidderSeats('adm')
  const { data: pbsBidderSeats, isLoading: isLoadingPbsSeats, refetch: refetchPbsSeats } = useBidderSeats('pbs')
  // Onboarded, and access or staff-on: owners and users of a staff-only scope see the card locked, as if never bought.
  const { canCreateSeat: canCreateSparkForPubSeat, isStaffOnSparkForPub } = useSparkForPubStaffAccess()
  // Onboarded and visible to this viewer: staff-only Spark reads as off to owners, usable (shielded) for admins.
  const { viewerOnboarded: isSparkOnboarded, isStaffOnSpark } = useSparkStaffAccess()
  // Asked of the scope, not the viewer: a Pubstack user has every feature synthesised for the account
  // they are in, and would otherwise see four creatable integrations on a scope that has bought none.
  // These are the **access** features on purpose — creating a seat is a write, and a scope can be
  // provisioned well before its users are granted anything.
  const hasAdManagement = scope.hasFeature(UPGRADE_OFFER_FEATURE.one)
  const { hasNodeAccess: hasNode, isStaffOnNode, isLockedToStaff } = useNodeStaffAccess()

  const bidderSeats = useMemo(() => [...admBidderSeats, ...pbsBidderSeats], [admBidderSeats, pbsBidderSeats])
  const isLoadingBidderSeats = isLoadingAdmSeats || isLoadingPbsSeats
  const refetchBidderSeats = () => {
    refetchAdmSeats()
    refetchPbsSeats()
  }

  /**
   * The Featured row: what to look at first, on every visit rather than only on an empty scope. Picked
   * from the whole catalog rather than from whatever the search and type filters currently narrow it to,
   * and recomputed only when the catalog or the scope's entitlements actually change — not on every
   * keystroke in the search box.
   *
   * Spark is pinned whether or not this scope can switch it on: it is the one entry Pubstack is selling
   * rather than merely integrating, and a scope that cannot have it yet is exactly who the pitch is for.
   * The two it keeps company with are Spark for Pubs bidders the reader could add today, topped up from
   * `FEATURED_FALLBACKS`. Already-deployed bidders are left out.
   *
   * `Math.random` is impure, so the pick happens in an effect rather than during render (React requires
   * render to be pure and side-effect-free) — the row is briefly empty on first paint rather than
   * randomised synchronously with it.
   */
  const [featuredIds, setFeaturedIds] = useState<Set<string>>(new Set())
  useEffect(() => {
    const seatedIds = new Set(bidderSeats.filter((bidderSeat) => bidderSeat.scopeId === scopeId).map((bidderSeat) => bidderSeat.bidderCode))
    const creatable = modules
      .filter((module) => {
        if (!module.isSparkForPubCompatible || seatedIds.has(module.id)) return false
        const { integrations, lockedIntegrations, lockedOffers, lockedReason } = seatCardAvailability({
          bidder: module,
          hasAdManagement,
          hasNode: hasNode || isStaffOnNode,
          canCreateSparkForPubSeat,
          isSparkOnboarded,
        })
        return integrations.length > lockedIntegrations.length && lockedOffers.length === 0 && !lockedReason
      })
      .map((module) => module.id)

    // Fisher-Yates. `sort(() => Math.random() - 0.5)` is not a shuffle: the comparator is inconsistent, so
    // the result is implementation-defined and V8's insertion sort leaves short arrays near their input
    // order — which here is alphabetical.
    const picks = creatable
      .reduce<string[]>((shuffled, id) => {
        const at = Math.floor(Math.random() * (shuffled.length + 1))
        shuffled.splice(at, 0, id)
        return shuffled
      }, [])
      .slice(0, FEATURED_RANDOM_COUNT)

    const topUp = FEATURED_FALLBACKS.map((names) => modules.find((module) => names.includes(module.id.toLowerCase()) || names.includes(module.displayName.toLowerCase()))?.id)
      .filter((id) => id !== undefined)
      .filter((id) => !seatedIds.has(id) && !picks.includes(id))

    const next = new Set([PBSTCK_BIDDER_SEAT_CODE, ...[...picks, ...topUp].slice(0, FEATURED_RANDOM_COUNT)])
    // Same ids, same object: `modules` is a fresh `[]` on every render while the catalog loads, and a
    // new Set each time would keep re-rendering the page into the same state forever.
    // oxlint-disable-next-line react/set-state-in-effect -- the pick depends on `Math.random`, which is impure and cannot run during render
    setFeaturedIds((current) => (sameIds(current, next) ? current : next))
  }, [modules, bidderSeats, scopeId, hasAdManagement, hasNode, isStaffOnNode, canCreateSparkForPubSeat, isSparkOnboarded])

  // Hooks
  const breadcrumbs = useBreadcrumbs()
  const navigate = useNavigate()
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)
  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()
  const toast = useToast()
  const { canCreate, disabledReason } = useScopePermissions()

  // The whole catalog, nothing held back: it is a display of what Pubstack integrates, and what a given
  // scope may do with each entry is already carried by the badges. A bidder that forbids aliasing used to
  // be dropped here, which turned "you cannot create a seat on this" into "Pubstack does not have it".
  const modulesWithSeatsCount = modules.map((e) => ({
    ...e,
    seatsCount: bidderSeats.filter((bidderSeat) => bidderSeat.bidderCode === e.id && bidderSeat.scopeId === scopeId).length,
  }))
  const filteredModules = modulesWithSeatsCount
    .filter((e) => e.displayName.toLowerCase().includes(search.toLowerCase()))
    // The badge is shown to everyone, but filtering by Spark is a way of working with seats you can
    // create — so this stays on the feature, in step with the Type dropdown that drives it.
    .filter((e) => (typeFilter === 'sparkForPub' && canCreateSparkForPubSeat ? e.isSparkForPubCompatible : true))

  const handleModuleCardClick = (bidderCatalog: BidderCatalog) => {
    flyout.open(
      CreateSeatFlyout,
      { color: Colors.Kaiminus, width: `${FLYOUT_WIDTH_PX}px`, closeTrackingLabel: `${ADD_MODULE_TRACKING_LABEL}-close` },
      {
        scopeId,
        type: 'pbs',
        preselectedBidderCatalog: bidderCatalog,
        // Which product the seat landed in no longer decides where the user is sent: there is one seat
        // list for both, so the callback's `product` argument is not needed here any more.
        onSeatCreated: (name: string) => {
          refetchBidderSeats()

          toast.success(
            <>
              <strong>{name}</strong> successfully added to your integrations. Go to{' '}
              <Link
                onClick={() => navigate({ pathname: '/monetization/inventory/my-bidder-seats', search: searchParams.toString() })}
                label="My bidder seats"
                trackingLabel={GO_TO_MY_BIDDER_SEATS_TRACKING_LABEL}
              />{' '}
              to configure it.
            </>,
            {
              duration: SEAT_CREATED_TOAST_DURATION_MILLIS,
            }
          )
        },
        onClose: () => flyout.close(),
      }
    )
  }

  /**
   * A bidder the scope cannot add yet: the same offer every other locked surface shows, over the catalog
   * rather than instead of it — the page is worth browsing whether or not anything on it can be clicked.
   *
   * Spark Demand used to be the exception twice over: first opening the HubSpot form with no explanation of
   * what had just been clicked, then getting a card of its own because it carries no `UpgradeOffer` — it is
   * switched on per scope by the Spark team. Neither survives. An unavailable entry is an unavailable entry,
   * and it gets the same card with the bidder named, whether or not a product name is attached to it.
   */
  const handleLockedModuleCardClick = (bidderCatalog: BidderCatalog, offers: UpgradeOffer[]) => {
    // `bare` so the offer card is the modal rather than a card inside one — see `UpgradeModal`.
    modal.open(UpgradeModal, { offers, context: `${bidderCatalog.displayName} is not available on your account yet.` }, { bare: true })
  }

  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        backToSite ? (
          // The Node activation page is the only thing that ever sets `backToSite`, so the media it
          // names is a Node one. This page no longer has a product of its own to infer it from.
          <Button trackingLabel={BACK_TO_SITE_TRACKING_LABEL} onClick={() => navigate(`${PATH_BY_PRODUCT.pbs}/sites/${backToSite}`)} variant="secondary">
            Back to site
          </Button>
        ) : undefined
      }
    >
      <Container>
        {/* One row, search first: the chips narrow whatever the search already returned, so reading them
            before it would be reading the qualifier before the thing it qualifies. */}
        <div className={'flex flex-wrap items-center gap-4'}>
          <FilterBar
            onSearchChange={(e) => setSearch(e)}
            // No status dropdown of its own — the chips are the status filter, so there is no status to
            // change. Reset still runs through here so one button clears the search, the type and the
            // chips together.
            onResetFilters={() => {
              setSection('all')
            }}
            resetFiltersTrackingLabel={RESET_FILTERS_TRACKING_LABEL}
            // Spark for Pubs is the only type there is to filter by, so the dropdown appears for whoever
            // can create one and on nothing else. Node used to be asked here too, which hid the filter on
            // exactly the scopes the seat is provisioned for.
            typeOptions={canCreateSparkForPubSeat ? TYPE_OPTIONS : undefined}
            typeLabel="Type"
            onTypeChange={(e) => setTypeFilter(e)}
          />

          <Chip.Group multiple={false} value={section} onChange={(value) => setSection(value as SectionFilter)}>
            <div className={'flex flex-wrap gap-2'}>
              {SECTION_FILTERS.map(({ value, label, trackingLabel }) => (
                <Chip key={value} value={value} variant={'outline'} size={'sm'} data-tracking={trackingLabel}>
                  {label}
                </Chip>
              ))}
            </div>
          </Chip.Group>
        </div>

        {isLoadingCatalog || isLoadingBidderSeats ? (
          <>
            <CatalogWrapper>
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} bigger width={`${MODULE_CARD_WIDTH_PX}px`} />
              ))}
            </CatalogWrapper>
          </>
        ) : modules.length === 0 ? (
          <IllustrationMessage iconName="night_sky" iconSize={`${ILLUSTRATION_ICON_SIZE_PX}px`} title="Oops" message="No modules found." />
        ) : filteredModules.length === 0 ? (
          <IllustrationMessage
            iconName="night_sky"
            iconSize={`${ILLUSTRATION_ICON_SIZE_PX}px`}
            title="Oops"
            // Where the "missing bidder?" line lives now. It used to sit above the grid on every visit;
            // the moment it is actually useful is the moment a search comes back empty.
            message={
              <EmptyStateMessage>
                <div>We can’t find what you seek. Please try another module.</div>
                <div>
                  If you work with a bidder not available in our catalog, please contact{' '}
                  <Link label={PRODUCT_MAIL} href={`mailto:${PRODUCT_MAIL}`} trackingLabel={PRODUCT_MAIL_TRACKING_LABEL} variant="secondary" />.
                </div>
              </EmptyStateMessage>
            }
          />
        ) : (
          (() => {
            const withAvailability = filteredModules.map((module) => {
              const availability = seatCardAvailability({ bidder: module, hasAdManagement, hasNode: hasNode || isStaffOnNode, canCreateSparkForPubSeat, isSparkOnboarded })
              const lockedReason = isLockedToStaff ? STAFF_ONLY_REASON : availability.integrations.length === 0 ? NO_SETUP_OPTIONS_REASON : availability.lockedReason
              return { module, ...availability, lockedReason, isLockedForCustomer: availability.lockedOffers.length > 0 || !!lockedReason }
            })

            /* Featured only stands apart in the unfiltered view: pick a chip and the reader has asked for
               one bucket in full, and holding three of its entries back in a row above it would be a
               list that quietly disagrees with its own heading. Every card lands in exactly one section
               either way. */
            const isAllView = section === 'all'
            const isFeatured = (m: (typeof withAvailability)[number]) => isAllView && featuredIds.has(m.module.id)

            // Sorted by seat count rather than left in catalog order: a scope's own deployments are the
            // most relevant thing on the page, and the sort is stable so ties keep the alphabetical order
            // the API already returns.
            const bySeatsDesc = (a: (typeof withAvailability)[number], b: (typeof withAvailability)[number]) => b.module.seatsCount - a.module.seatsCount
            const featured = withAvailability.filter(isFeatured)
            const installed = withAvailability.filter((m) => m.module.seatsCount > 0 && !isFeatured(m)).sort(bySeatsDesc)
            // Deployed already counts as installed even where the entitlement behind it has since gone —
            // the seat is on the account, and calling it unavailable would contradict the seat list.
            const available = withAvailability.filter((m) => m.module.seatsCount === 0 && !m.isLockedForCustomer && !isFeatured(m)).sort(bySeatsDesc)
            const unavailable = withAvailability.filter((m) => m.module.seatsCount === 0 && m.isLockedForCustomer && !isFeatured(m)).sort(bySeatsDesc)

            const renderCard = (entry: (typeof withAvailability)[number]) => {
              const { module, integrations, lockedIntegrations, lockedOffers, lockedReason, isLockedForCustomer } = entry
              const canCreateSomething = integrations.length > lockedIntegrations.length && !isLockedToStaff
              // Per card: Spark staff-only shields the Spark card only.
              const isShielded = isStaffOnNode || (module.code === PBSTCK_BIDDER_SEAT_CODE && isStaffOnSpark)
              /* A promoted card is never greyed out. The Featured row exists to put an offer in front of
                 someone, and the offers worth promoting are mostly the ones they have not bought — so the
                 dimming that reads as "not for you" landed on exactly the cards meant to read as "look at
                 this". Below the fold, out of reach still greys out: there the grey is what sorts the
                 sections, and nothing is being promoted. */
              const promoted = isFeatured(entry)
              return (
                <ModuleCard
                  key={module.id}
                  // The logo is the catalog entry's own brand, so it keys on the entry id — a partner
                  // riding another adapter must not show that adapter's logo.
                  code={module.id}
                  name={module.displayName}
                  description={module.tagline ?? (isBidder(module.type) ? derivedTagline(module.supportedMediaTypes ?? [...MEDIA_TYPES], integrations) : MODULE_TAGLINE)}
                  type={module.type}
                  mediaTypes={module.supportedMediaTypes ?? [...MEDIA_TYPES]}
                  envs={compatibleSiteEnvs(module.supportedEnvs)}
                  integrations={integrations}
                  lockedIntegrations={lockedIntegrations}
                  staffOnlyIntegrations={isStaffOnSparkForPub ? ['Spark for Pubs'] : undefined}
                  className={isShielded ? 'border-king hover:border-king' : undefined}
                  dimmed={isLockedForCustomer && !promoted}
                  // Names the click rather than leaving it to a lone icon: the card grew a second control
                  // when the compatibility moved behind a named trigger, and two unlabelled icons on one
                  // card is a guess the reader should not have to make. It is also what the compatibility
                  // sheet puts on its own button, so the wording has to be the act, not the object.
                  /* "Upgrade" for everything not yet owned, whether or not an `UpgradeOffer` names it: the
                     Spark team switches Spark seats on per scope, so they carry none. */
                  actionLabel={canCreateSomething ? 'Add seat' : isLockedToStaff ? 'Unavailable' : 'Upgrade'}
                  /* Upgrade is the one action on this page that asks for something not yet owned, and on a
                     promoted card it is the point of the card being there — so it reads as a button rather
                     than as one more blue phrase in a grid of them. */
                  actionVariant={promoted && !canCreateSomething ? 'cta' : 'link'}
                  /* Only the seat is refused, and only to a role that cannot create one. "Upgrade" stays
                     live for everybody: asking for a product the scope has not bought is not a write, and
                     a `user` is exactly who might want to ask. */
                  actionDisabled={(canCreateSomething && !canCreate) || isLockedToStaff}
                  footerIcon={
                    canCreateSomething ? (
                      /* The reason stands in for the label when the seat is out of this role's reach: the
                         card keeps saying what it is for, and the hover says who can act on it. */
                      <FlexTooltip title={canCreate ? (isShielded ? 'New seat (Pubstack staff only)' : 'New seat') : disabledReason}>
                        {/* Grey with the label beside it: the icon carries the same act, and left in King
                            it would be the one thing on a refused footer still offering to be pressed. */}
                        <Icon width="20px" fill={canCreate ? Colors.King : Colors.SlateGrey} name={isShielded ? 'admin' : 'add_bidder'} />
                      </FlexTooltip>
                    ) : (
                      /* A crown, not a padlock. Every card on this page is browsable and none of them is
                         forbidden — what separates these is that they have not been bought, which is the
                         one thing a lock does not say. Same glyph as the ad unit tabs and the blocked
                         rule sections, so it means the same thing wherever it appears. */
                      <FlexTooltip title={lockedReason ?? `Available with ${lockedOffers.map((offer) => UPGRADE_OFFER_LABEL[offer]).join(' and ')}`}>
                        <Icon width="20px" fill={Colors.King} name="crown" />
                      </FlexTooltip>
                    )
                  }
                  baseCDNUrl={STATIC_ASSETS_CDN}
                  trackingLabel={canCreateSomething ? ADD_MODULE_TRACKING_LABEL : LOCKED_MODULE_TRACKING_LABEL}
                  status={
                    module.seatsCount > 0 ? (
                      /* The count is the one thing on the card describing something the publisher already
                         has, so it carries its own click to where those live. Left to the card's, it fell
                         through to "add a seat" — the opposite of what a reader pressing "3 seats" wants.
                         The chevron is what says it leads somewhere before anyone has to try it. */
                      <button
                        type={'button'}
                        /* Padded out and pulled back by the same amount: the words sit exactly where they
                           did, but the target around them is a comfortable one rather than the height of
                           a line of small text — and it is the last thing on a card whose own click does
                           something else, so a near miss was not a no-op. */
                        className={'-m-2 flex cursor-pointer items-center gap-0.5 p-2 hover:underline'}
                        title={'See these seats in My bidder seats'}
                        data-tracking-label={GO_TO_MY_BIDDER_SEATS_TRACKING_LABEL}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          navigate({ pathname: '/monetization/inventory/my-bidder-seats', search: searchParams.toString() })
                        }}
                      >
                        <CatalogStatus state={'active'} font={Fonts.P2}>
                          {`${module.seatsCount} seat${module.seatsCount > 1 ? 's' : ''}`}
                        </CatalogStatus>
                        <Icon width={'14px'} fill={Colors.King} name={'chevron_right'} />
                      </button>
                    ) : (
                      <CatalogStatus state={'inactive'} font={Fonts.P2}>
                        {'No seat'}
                      </CatalogStatus>
                    )
                  }
                  /* Owning the product and being allowed to act on it are different questions: a `user` on a
                     scope with the product sees the same card as an owner and cannot open the seat flyout
                     from it. The offer modal is not the answer here — nothing is missing from the account. */
                  /* No `canCreate` guard here any more: the card is handed the refusal through
                     `actionDisabled`, which is what greys the footer and stops all four of its triggers at
                     once — a guard buried in the handler let the card go on looking live. */
                  onClick={() => (isLockedToStaff ? undefined : canCreateSomething ? handleModuleCardClick(module) : handleLockedModuleCardClick(module, lockedOffers))}
                />
              )
            }

            /* A section with nothing in it is left out entirely rather than headed over an empty grid —
               a scope entitled to nothing has every entry under Not available, and an empty Available
               heading would be the first thing it reads. */
            const sections = [
              { key: 'featured', title: 'Featured', cards: featured, shown: isAllView },
              { key: 'installed', title: 'Installed', cards: installed, shown: isAllView || section === 'installed' },
              { key: 'available', title: 'Available', cards: available, shown: isAllView || section === 'available' },
              { key: 'unavailable', title: 'Not available', cards: unavailable, shown: isAllView || section === 'unavailable' },
            ].filter((s) => s.shown && s.cards.length > 0)

            return sections.length === 0 ? (
              <IllustrationMessage iconName="night_sky" iconSize={`${ILLUSTRATION_ICON_SIZE_PX}px`} title="Oops" message="Nothing here yet. Try another filter." />
            ) : (
              <>
                {sections.map(({ key, title, cards }) => (
                  <Container key={key}>
                    <SectionTitle>{title}</SectionTitle>
                    <CatalogWrapper>{cards.map(renderCard)}</CatalogWrapper>
                  </Container>
                ))}
              </>
            )
          })()
        )}
      </Container>
    </PageContent>
  )
}
